import React, { useState } from "react";
import { Button, Input, Modal, Result } from "antd";
import { FiArrowLeft, FiDownload, FiRotateCcw } from "react-icons/fi";
import { useHistory, useLocation, useParams } from "react-router-dom";
import {
  brandPaymentStatusClass,
  brandPaymentStatusLabel,
} from "../../utils/brandPaymentStatus";
import {
  useBrandPaymentHistoryDetail,
  useBankTransferPaymentRefund,
  useBrandPaymentRefund,
  useBrandRejectedRefund,
} from "../../hooks/usePayment";
import {
  influencerPaymentStatusClass,
  influencerPaymentStatusLabel,
} from "../../utils/influencerPaymentStatus";
import { CATEGORY_MAPPING } from "../../utils/categories";
import { formatCurrency, parseApiAmount } from "../../utils/amountFormat";
import { getPaymentCardBrandLabel } from "../../utils/paymentCardBrand";
import { getPaymentMethodLabel } from "../../utils/paymentMethod";

const REFUND_CANCEL_TIMELINE_NOTICE =
  "회원님께서 결제하신 결제 수단을 통해 취소처리가 진행되며, 실제 취소 반영까지는 시간이 다소 소요될 수 있는 점 참고 부탁드립니다. (평균 평일기준 3-4일 소요)";

export const BrandPaymentHistoryDetail = () => {
  const history = useHistory();
  const { state: listPreview } = useLocation();
  const { payReportId } = useParams();
  const [refundModalOpen, setRefundModalOpen] = useState(false);
  const [refundReason, setRefundReason] = useState("");

  const listStatus = listPreview?.status;
  const cancelledAt = listPreview?.cancelled_at;
  const refundedAt = listPreview?.refunded_at;
  const createdAt = listPreview?.created_at;
  const campaignTitle = listPreview?.campaign_title;
  const campaignCategory = listPreview?.campaign_category;
  const paidAmount = listPreview?.paid_amount;
  const paidAt = listPreview?.paid_at;
  const merchantUid = listPreview?.merchant_uid;
  const payerName = listPreview?.payer_name;
  const payMethod = listPreview?.pay_method;
  const cardBrand = listPreview?.card_brand;
  const cardLast4 = listPreview?.card_last4
    ? `${String(listPreview.card_last4).slice(-4)}`
    : null;
  const currency = listPreview?.currency;
  const receiptUrl = listPreview?.pg_response_json?.receiptUrl;
  const shouldFetchRejectedRefund =
    listStatus === "paid" && Boolean(cancelledAt) && Boolean(payReportId);

  const {
    data: paymentDetail,
    isLoading,
    isError,
  } = useBrandPaymentHistoryDetail({
    payReportId,
  });

  const { data: rejectedRefund } = useBrandRejectedRefund(
    payReportId,
    shouldFetchRejectedRefund
  );

  const cardRefundMutation = useBrandPaymentRefund();
  const bankTransferRefundMutation = useBankTransferPaymentRefund();
  const isBankTransfer = payMethod === "bank_transfer";
  const refundMutation = isBankTransfer
    ? bankTransferRefundMutation
    : cardRefundMutation;
  const rejectedRefundItem = Array.isArray(rejectedRefund)
    ? rejectedRefund[0]
    : rejectedRefund;
  const showRejectedRefund =
    shouldFetchRejectedRefund && Boolean(rejectedRefundItem);
  const hasPaidInfluencer = paymentDetail?.some((row) => row.status === "paid");
  const hasCancelledSettlementLine = paymentDetail?.some(
    (row) => row.status === "brand_cancelled" || row.status === "cancelled"
  );

  const cardBrandLabel = getPaymentCardBrandLabel(cardBrand);
  const cardSummary =
    [cardBrandLabel, cardLast4].filter(Boolean).join(" · ") || "—";
  const hasCardSummary = Boolean(cardBrandLabel || cardLast4);

  const closeRefundModal = () => {
    setRefundModalOpen(false);
    setRefundReason("");
  };

  const handleConfirmRefund = () => {
    if (!payReportId) return;
    refundMutation.mutate({
      payReportId,
      refundReason: refundReason.trim() || "사유 없음",
    });
    closeRefundModal();
  };

  if (isLoading) {
    return null;
  }

  if (isError || !listPreview || !paymentDetail) {
    return (
      <div className="mx-auto max-w-[900px]">
        <Result
          status="warning"
          title="결제 내역을 찾을 수 없습니다."
          subTitle="목록에서 다시 상세 내역을 선택해 주세요."
          extra={
            <Button
              type="primary"
              onClick={() =>
                history.replace("/payments/brand-payments-history")
              }
            >
              결제 내역으로 이동
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="w-full max-w-[1200px]">
      <h2 className="md:text-4xl text-3xl font-bold tracking-[-0.03em] text-[#111111]">
        결제 내역 상세
      </h2>
      <Button
        type="button"
        onClick={() => history.goBack()}
        className="mb-5 text-lg md:text-xl inline-flex items-center gap-2 border border-[#e1e6ec] bg-white px-4 py-2 font-semibold text-[#343a40]"
      >
        <FiArrowLeft className="text-lg md:text-xl" />
        목록으로 돌아가기
      </Button>

      <section className="mb-6 rounded-[15px] border border-[#e7eaee] bg-white p-5 shadow-[0_14px_36px_rgba(17,24,39,0.05)] md:p-6">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="mb-3 flex flex-wrap items-center gap-3">
              <span
                className={`inline-flex rounded-[8px] px-2.5 py-1 text-[12px] font-bold ${brandPaymentStatusClass(
                  listStatus
                )}`}
              >
                {brandPaymentStatusLabel(listStatus)}
              </span>
              {shouldFetchRejectedRefund && (
                <span
                  className={`inline-flex rounded-[8px] px-2.5 py-1 text-[12px] font-bold bg-[#f1f5f9] text-[#64748b]`}
                >
                  환불 거절
                </span>
              )}
            </div>

            <p className="break-keep text-[18px] font-bold leading-[1.28] tracking-[-0.04em] text-[#111827] md:text-[24px]">
              {campaignTitle}
            </p>

            <p className="mt-1.5 text-[13px] text-[#64748b]">
              {CATEGORY_MAPPING[campaignCategory]}
            </p>
          </div>

          <div className="rounded-[18px] bg-[#111827] px-5 py-4 text-white lg:min-w-[240px]">
            <div className="text-[12px] font-medium text-white">총 금액</div>
            <div className="mt-1.5 text-[20px] font-bold tracking-[-0.05em] text-end">
              {formatCurrency(parseApiAmount(paidAmount), currency)}
            </div>
          </div>
        </div>
      </section>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_300px]">
        <section className="rounded-[15px] border border-[#e7eaee] bg-white p-5 shadow-[0_10px_28px_rgba(17,24,39,0.04)] md:p-6">
          <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h3 className="text-[17px] font-bold tracking-[-0.03em] text-[#111827]">
                인플루언서별 정산
              </h3>
              <p className="mt-1 text-[13px] text-[#7a7f87]">
                캠페인에 참여한 인플루언서별 실제 지급 금액입니다.
              </p>
            </div>
            <span className="w-fit rounded-full bg-[#f1f5f9] px-3 py-1 text-[12px] font-semibold text-[#64748b]">
              총 {paymentDetail.length}명
            </span>
          </div>

          <div className="space-y-2.5">
            {paymentDetail.map((row) => (
              <div
                key={row.id}
                className="flex flex-col gap-2 rounded-lg bg-[#f8fafc] px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="min-w-0 flex-1">
                  <span
                    className={`inline-flex rounded-[8px] px-2 py-0.5 text-[11px] font-bold ${influencerPaymentStatusClass(
                      row.status
                    )}`}
                  >
                    {influencerPaymentStatusLabel(row.status)}
                  </span>
                  <div className="mt-1.5 truncate text-[14px] font-bold text-[#343a40] px-1 py-1">
                    {row.nickname}
                  </div>
                  {row.paid_at ? (
                    <div className="text-[11px] text-[#94a3b8]">
                      지급일 {row.paid_at}
                    </div>
                  ) : null}
                </div>
                <div className="flex flex-col sm:items-end sm:text-right">
                  <span className="text-[10px] font-semibold text-[#7a7f87]">
                    실 지급액
                  </span>
                  <span className="text-[15px] font-bold tracking-[-0.03em] tabular-nums text-[#111827]">
                    {formatCurrency(
                      parseApiAmount(row.net_amount),
                      row.currency ?? currency
                    )}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </section>

        <aside className="space-y-4">
          <section className="rounded-[15px] border border-[#e7eaee] bg-white p-5 shadow-[0_10px_28px_rgba(17,24,39,0.04)]">
            <h3 className="text-[15px] font-bold text-[#111827]">결제 정보</h3>
            <div className="mt-4 space-y-2.5 text-[13px] text-[#64748b]">
              <div className="flex items-baseline justify-between gap-4">
                <span className="shrink-0">주문 번호</span>
                <span className="text-right font-semibold text-[#343a40]">
                  {merchantUid?.slice(4) ?? "-"}
                </span>
              </div>
              <div className="flex items-baseline justify-between gap-4">
                <span className="shrink-0">결제 일시</span>
                <span className="text-right font-semibold text-[#343a40]">
                  {paidAt ?? createdAt}
                </span>
              </div>
              <div className="flex items-baseline justify-between gap-4">
                <span className="shrink-0">결제 상태</span>
                <span className="text-right font-semibold text-[#343a40]">
                  {brandPaymentStatusLabel(listStatus)}
                </span>
              </div>
              {listStatus === "refunded" && (
                <div className="flex items-baseline justify-between gap-4">
                  <span className="shrink-0">환불 일시</span>
                  <span className="text-right font-semibold text-[#343a40]">
                    {refundedAt}
                  </span>
                </div>
              )}
              <div className="flex items-baseline justify-between gap-4">
                <span className="shrink-0">결제 수단</span>
                <span className="text-right font-semibold text-[#343a40]">
                  {hasCardSummary
                    ? cardSummary
                    : getPaymentMethodLabel(payMethod)}
                </span>
              </div>
              <div className="flex items-baseline justify-between gap-4">
                <span className="shrink-0">결제자</span>
                <span className="text-right font-semibold text-[#343a40]">
                  {payerName ?? "-"}
                </span>
              </div>
            </div>

            {showRejectedRefund ? (
              <div className="mt-5 border-t border-[#e7eaee] pt-5">
                <div className="mb-3 inline-flex font-bold text-[#c01048]">
                  환불 신청이 거절되었습니다
                </div>

                <div className="space-y-2.5 text-[13px] text-[#64748b]">
                  <div className="flex items-start justify-between gap-4">
                    <span className="shrink-0">환불 상태</span>
                    <span className="font-semibold text-[#343a40]">
                      {rejectedRefundItem?.refund_status === "failed"
                        ? "환불 거절"
                        : (rejectedRefundItem?.refund_status ?? "-")}
                    </span>
                  </div>
                  <div className="flex items-start justify-between gap-4">
                    <span className="shrink-0">환불 신청 사유</span>
                    <span className="font-semibold text-[#343a40]">
                      {rejectedRefundItem?.refund_reason ?? "-"}
                    </span>
                  </div>
                  <div className="flex items-start justify-between gap-4">
                    <span className="shrink-0">거절 처리 일시</span>
                    <span className="font-semibold text-[#343a40]">
                      {rejectedRefundItem?.failed_at ?? "-"}
                    </span>
                  </div>
                </div>

                <p className="mt-3 text-[12px] leading-[1.6] text-[#7a7f87]">
                  카드사 및 PG사 정책에 따라 취소 가능 기한과 처리 기준이 다를
                  수 있으며, 취소 가능 기한 초과, 이미 취소된 거래, 취소 가능
                  금액 초과, 카드 상태 또는 결제 조건 등의 사유로 환불이 거절될
                  수 있습니다.
                </p>
              </div>
            ) : listStatus === "paid" &&
              !hasPaidInfluencer &&
              !hasCancelledSettlementLine ? (
              <ul className="mt-5 border-t border-[#e7eaee] pt-5">
                <li className="mb-2 text-[12px] leading-[1.55] text-black">
                  • 결제일로부터 7일 이내이고 크리에이터에게 정산이 진행되지
                  않은 경우 환불이 가능합니다.
                </li>
                <li className="mb-3 text-[12px] leading-[1.55] text-black">
                  • {REFUND_CANCEL_TIMELINE_NOTICE}
                </li>
                {/* <button
                  type="button"
                  onClick={() => {
                    setRefundModalOpen(true);
                  }}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-[#ffccc7] bg-[#fff2f0] px-4 py-2.5 text-[13px] font-semibold text-[#cf1322] transition-all hover:border-[#ffa39e] hover:bg-[#ffe7e3]"
                >
                  <FiRotateCcw className="text-[15px]" />
                  결제 취소
                </button> */}
                <div className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-[#ffccc7] bg-[#fff2f0] px-4 py-2.5 text-[13px] font-semibold text-[#cf1322]">
                  결제 취소를 원하시는 경우, 1:1 문의 또는
                  이메일(support@surfingcre.com) 문의해 주세요.
                </div>
              </ul>
            ) : listStatus === "cancelled" || listStatus === "refunded" ? (
              <div className="mt-5 border-t border-[#e7eaee] pt-5">
                <p className="text-[12px] text-center leading-[1.55] text-[#cf1322]">
                  {listStatus === "cancelled"
                    ? "결제가 취소되었습니다."
                    : "환불이 완료되었습니다."}
                </p>
              </div>
            ) : null}
          </section>

          {receiptUrl && (
            <section className="rounded-[15px] border border-[#e7eaee] bg-white p-5 shadow-[0_10px_28px_rgba(17,24,39,0.04)]">
              <h3 className="text-[15px] font-bold text-[#111827]">문서</h3>
              <button
                type="button"
                className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-lg border border-[#d2d8e0] bg-white px-4 py-2.5 text-[13px] font-semibold text-[#2d333a] transition-all hover:border-[#b7bec8] hover:bg-[#f9fafb]"
                onClick={() => window.open(receiptUrl, "_blank")}
              >
                <FiDownload className="text-[15px]" />
                영수증
              </button>
            </section>
          )}
        </aside>
      </div>

      <Modal
        title={
          <span className="text-[17px] font-bold text-[#111827]">
            환불을 진행할까요?
          </span>
        }
        open={refundModalOpen}
        onCancel={() => {
          if (!refundMutation.isPending) closeRefundModal();
        }}
        footer={null}
        width={440}
        destroyOnClose
        centered
      >
        <div className="pt-1">
          <p className="text-[14px] leading-[1.55] text-[#5b616a]">
            아래 건이 결제 취소됩니다. 진행한 뒤에는 되돌릴 수 없습니다.
          </p>
          <div className="mt-4 space-y-2.5 rounded-[12px] bg-[#f8fafc] px-4 py-3 text-[13px]">
            <div className="flex items-baseline justify-between gap-3">
              <span className="shrink-0 text-[#64748b]">총 결제 취소 금액</span>
              <span className="font-bold text-[#111827]">
                {formatCurrency(parseApiAmount(paidAmount), currency)}
              </span>
            </div>
            {merchantUid ? (
              <div className="flex items-baseline justify-between gap-3">
                <span className="shrink-0 text-[#64748b]">주문 번호</span>
                <span className="text-right font-semibold text-[#343a40]">
                  {merchantUid?.slice(4)}
                </span>
              </div>
            ) : null}
          </div>
          <div className="mt-5">
            <label
              className="mb-1.5 block text-[13px] font-semibold text-[#343a40]"
              htmlFor="brand-refund-reason"
            >
              결제 취소 사유
            </label>
            <Input.TextArea
              id="brand-refund-reason"
              value={refundReason}
              onChange={(e) => setRefundReason(e.target.value)}
              placeholder="결제 취소 사유를 구체적으로 적어 주세요."
              maxLength={100}
              showCount
              rows={1}
              disabled={refundMutation.isPending}
              className="text-[14px]"
            />
          </div>
          <div className="mt-12 flex flex-col-reverse gap-2.5 sm:flex-row sm:justify-end">
            <Button
              onClick={closeRefundModal}
              disabled={refundMutation.isPending}
            >
              취소
            </Button>
            <Button
              type="primary"
              danger
              loading={refundMutation.isPending}
              onClick={handleConfirmRefund}
            >
              확인
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
